<template>
  <div class="directory-tree-item">
    <!-- Текущий элемент -->
    <div
      class="directory-item"
      :class="{
        'is-selected': isSelected,
        'is-category': item.is_subcategory,
        'is-value': !item.is_subcategory,
      }"
      @click="handleItemClick"
    >
      <div class="item-indent" :style="{ width: `${depth * 20}px` }"></div>

      <div
        class="item-icon"
        :class="item.is_subcategory ? '' : 'bg-gray-100 border-gray-200'"
        p
      >
        <button
          v-if="item.is_subcategory"
          class="expand-btn"
          @click.stop="toggleExpand"
          :title="isExpanded ? $t('Свернуть') : $t('Раскрыть')"
        >
          <svg-icon
            :name="isExpanded ? 'huge-shevron-down' : 'huge-shevron-right'"
            class="w-3 h-3 text-gray-600"
          />
        </button>
      </div>

      <div class="item-content" @click="selectItem">
        <div class="item-name" :title="getDisplayName(item)">
          {{ getDisplayName(item) }}
        </div>

        <!--        <span-->
        <!--          v-if="item.is_subcategory && childrenCount > 0"-->
        <!--          class="children-badge"-->
        <!--        >-->
        <!--          {{ childrenCount }}-->
        <!--        </span>-->
      </div>

      <div class="item-actions">
        <button
          class="action-btn"
          @click.stop="showEditDialog"
          :title="$t('Редактировать')"
        >
          <svg-icon name="huge-edit" class="w-3.5 h-3.5 text-gray-700" />
        </button>
        <button
          class="action-btn"
          @click.stop="showAddDialog"
          :title="$t('Добавить дочерний')"
        >
          <svg-icon name="huge-plus" class="w-3.5 h-3.5 text-gray-700" />
        </button>
        <button
          class="action-btn"
          @click.stop="confirmDelete"
          :title="$t('Удалить')"
        >
          <svg-icon name="huge-danger" class="w-3.5 h-3.5 text-red-400" />
        </button>
      </div>
    </div>

    <div
      v-if="isExpanded && children && children.length > 0"
      class="children-container"
    >
      <DirectoryTreeItem
        v-for="child in children"
        :key="child.code"
        :item="child"
        :depth="depth + 1"
        :selected-item-code="selectedItemCode"
        :expanded-items="expandedItems"
        :children-data="childrenData"
        @select="handleChildSelect"
        @edit="handleChildEdit"
        @add-child="handleChildAdd"
        @delete="handleChildDelete"
      />
    </div>

    <div
      v-if="isLoading && isExpanded"
      class="flex justify-center items-center py-8"
    >
      <Spinner size="sm" />
    </div>
  </div>
</template>

<script>
import { getDirectoryChildren } from "@/entities/directory";
import { Spinner } from "@/shared/ui/spinner";

export default {
  name: "DirectoryTreeItem",
  components: { Spinner },

  props: {
    item: {
      type: Object,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    selectedItemCode: {
      type: String,
      default: null,
    },
    loadChildrenOnExpand: {
      type: Boolean,
      default: true,
    },
    // Массив раскрытых элементов (для управления из родителя)
    expandedItems: {
      type: Array,
      default: () => [],
    },
    // Кэшированные данные детей (для быстрой загрузки)
    childrenData: {
      type: Object,
      default: () => ({}),
    },
    // Внешнее управление раскрытием
    forceExpand: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["select", "edit", "add-child", "delete", "expand", "collapse"],

  data() {
    return {
      isExpanded: false,
      children: [],
      isLoading: false,
      hasLoadedChildren: false,
      localChildrenCache: [],
    };
  },

  computed: {
    isSelected() {
      return this.selectedItemCode === this.item.code;
    },

    childrenCount() {
      // Используем внешние данные или локальные
      return this.item.children_count || this.children.length;
    },

    // Проверяем, есть ли этот элемент в массиве раскрытых
    isExternallyExpanded() {
      return this.expandedItems.includes(this.item.code);
    },
  },

  watch: {
    forceExpand: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.isExpanded) {
          this.isExpanded = true;
          this.loadChildrenIfNeeded();
        }
      },
    },

    isExpanded(newVal) {
      if (newVal) {
        this.$emit("expand", this.item.code);
        this.loadChildrenIfNeeded();
      } else {
        this.$emit("collapse", this.item.code);
      }
    },

    // Следим за внешним управлением раскрытием
    isExternallyExpanded: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.isExpanded) {
          this.isExpanded = newVal;
        }
      },
    },

    // Следим за кэшированными данными детей
    childrenData: {
      deep: true,
      immediate: true,
      handler(newData) {
        if (newData[this.item.code]) {
          this.children = newData[this.item.code];
          this.hasLoadedChildren = true;
          this.$forceUpdate();
        }
      },
    },
  },

  created() {
    // Если в кэше уже есть данные, используем их
    if (this.childrenData[this.item.code]) {
      this.children = this.childrenData[this.item.code];
      this.hasLoadedChildren = true;
    }
  },

  methods: {
    getDisplayName(item) {
      return (
        item.name ||
        item.name_kz ||
        item.name_en ||
        item.code ||
        this.$t("Без названия")
      );
    },

    toggleExpand() {
      if (!this.item.is_subcategory) return;
      this.isExpanded = !this.isExpanded;
    },

    async loadChildrenIfNeeded() {
      if (
        !this.item.is_subcategory ||
        this.hasLoadedChildren ||
        this.isLoading
      ) {
        return;
      }

      await this.loadChildren();
    },

    async loadChildren() {
      if (!this.item.is_subcategory) return;

      this.isLoading = true;
      try {
        const data = await getDirectoryChildren(this.item.code, {
          page: 1,
          size: 100,
        });

        if (data?.success && data.payload?.items) {
          this.children = data.payload.items;
          this.hasLoadedChildren = true;

          // Уведомляем родителя о загруженных данных
          this.$emit("children-loaded", {
            parentCode: this.item.code,
            children: data.payload.items,
          });
        }
      } catch (error) {
        console.error(`Ошибка загрузки детей для ${this.item.code}:`, error);
      } finally {
        this.isLoading = false;
      }
    },

    selectItem() {
      this.$emit("select", this.item);
    },

    handleItemClick() {
      this.selectItem();
    },

    showEditDialog() {
      this.$emit("edit", this.item);
    },

    showAddDialog() {
      this.$emit("add-child", this.item);
    },

    confirmDelete() {
      this.$emit("delete", this.item);
    },

    handleChildSelect(item) {
      this.$emit("select", item);
    },

    handleChildEdit(item) {
      this.$emit("edit", item);
    },

    handleChildAdd(item) {
      this.$emit("add-child", item);
    },

    handleChildDelete(item) {
      this.$emit("delete", item);
    },

    // Метод для принудительной перезагрузки детей
    async reloadChildren() {
      this.hasLoadedChildren = false;
      this.children = [];
      if (this.isExpanded) {
        await this.loadChildren();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.directory-tree-item {
  position: relative;
  width: 400px;

  .directory-item {
    @apply flex items-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer;
    position: relative;
    min-height: 40px;

    &:hover {
      background: rgb(var(--color-gray-50));
    }

    &.is-selected {
      background: rgb(var(--color-gray-100));

      &:hover {
        background: rgb(var(--color-gray-100));
      }
    }

    &.is-category {
      @apply font-medium;
    }

    &.is-value {
      @apply text-gray-700;
    }

    .item-indent {
      @apply flex-shrink-0;
    }

    .expand-btn {
      @apply flex items-center justify-center;
    }

    .expand-placeholder {
      @apply w-4 h-4 flex-shrink-0;
    }

    .item-icon {
      @apply w-4 h-4 flex-shrink-0 flex items-center justify-center border border-gray-300;
      border-radius: 4px;
    }

    .item-content {
      @apply flex-1 flex items-center gap-3 min-w-0;
      cursor: pointer;
      position: relative;
      overflow: hidden;

      .item-name {
        @apply text-gray-900 truncate flex-1;
        max-width: none;
        transition: padding-right 0.2s ease;
      }

      .children-badge {
        @apply text-xs bg-gray-900/5 text-gray-500 px-1.5 py-0.5 rounded flex-shrink-0;
      }
    }

    .item-actions {
      @apply flex items-center gap-1 transition-all flex-shrink-0;
      opacity: 0;
      transform: translateX(10px);
      pointer-events: none;
      //transition: opacity 0.2s ease, transform 0.2s ease;
      margin-left: auto; // Выталкиваем вправо
      position: relative;
      z-index: 1;
      padding-left: 3px;

      // Градиент для плавного перехода
      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: -2px;
        bottom: -2px;
        width: 20px;
        background: linear-gradient(to right, transparent, inherit);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
      }

      .action-btn {
        @apply p-1 rounded hover:bg-gray-200 flex-shrink-0;
        pointer-events: auto;
      }
    }

    &:hover {
      .item-actions {
        opacity: 1;
        transform: translateX(0);
        pointer-events: auto;
      }

      .item-actions::before {
        opacity: 1;
      }

      .item-content .item-name {
        padding-right: 5px;
      }
    }
  }
}
</style>
